import type { ReactNode } from "react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { EmptyState, Loading } from "./components/ui";
import { AppShell } from "./layout/AppShell";

const DashboardPage = lazy(() => import("./features/dashboard/DashboardPage"));
const ProductsPage = lazy(() => import("./features/catalog/ProductsPage"));
const ProductDetailPage = lazy(() => import("./features/catalog/ProductDetailPage"));
const ScreenEditorPage = lazy(() => import("./features/catalog/ScreenEditorPage"));
const CustomersPage = lazy(() => import("./features/customers/CustomersPage"));
const CustomerDetailPage = lazy(() => import("./features/customers/CustomerDetailPage"));
const KnowledgePage = lazy(() => import("./features/knowledge/KnowledgePage"));
const ProvidersPage = lazy(() => import("./features/ai/ProvidersPage"));
const ModelsPage = lazy(() => import("./features/ai/ModelsPage"));
const PoliciesPage = lazy(() => import("./features/ai/PoliciesPage"));
const SimulatorPage = lazy(() => import("./features/ai/SimulatorPage"));
const PlaygroundPage = lazy(() => import("./features/assistant/PlaygroundPage"));
const ConversationsPage = lazy(() => import("./features/assistant/ConversationsPage"));
const ConversationDetailPage = lazy(() => import("./features/assistant/ConversationDetailPage"));
const RunsPage = lazy(() => import("./features/assistant/RunsPage"));
const RunDetailPage = lazy(() => import("./features/assistant/RunDetailPage"));
const FeedbackPage = lazy(() => import("./features/assistant/FeedbackPage"));
const UsagePage = lazy(() => import("./features/usage/UsagePage"));
const UsersPage = lazy(() => import("./features/admin/UsersPage"));
const AuditPage = lazy(() => import("./features/admin/AuditPage"));
const SettingsPage = lazy(() => import("./features/admin/SettingsPage"));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Guard({ perm, children }: { perm: string; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) return <EmptyState title="Bu sayfa için yetkiniz yok" description={`Gerekli izin: ${perm}`} />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          path="*"
          element={
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route index element={<DashboardPage />} />
                <Route path="catalog" element={<Guard perm="catalog.read"><ProductsPage /></Guard>} />
                <Route path="catalog/:productId" element={<Guard perm="catalog.read"><ProductDetailPage /></Guard>} />
                <Route path="catalog/:productId/screens/:screenId" element={<Guard perm="catalog.read"><ScreenEditorPage /></Guard>} />
                <Route path="customers" element={<Guard perm="customers.read"><CustomersPage /></Guard>} />
                <Route path="customers/:customerId" element={<Guard perm="customers.read"><CustomerDetailPage /></Guard>} />
                <Route path="knowledge" element={<Guard perm="knowledge.read"><KnowledgePage /></Guard>} />
                <Route path="ai/providers" element={<Guard perm="ai.read"><ProvidersPage /></Guard>} />
                <Route path="ai/models" element={<Guard perm="ai.read"><ModelsPage /></Guard>} />
                <Route path="ai/policies" element={<Guard perm="ai.read"><PoliciesPage /></Guard>} />
                <Route path="ai/simulator" element={<Guard perm="ai.read"><SimulatorPage /></Guard>} />
                <Route path="assistant/playground" element={<Guard perm="assistant.playground"><PlaygroundPage /></Guard>} />
                <Route path="assistant/conversations" element={<Guard perm="conversations.read"><ConversationsPage /></Guard>} />
                <Route path="assistant/conversations/:id" element={<Guard perm="conversations.read"><ConversationDetailPage /></Guard>} />
                <Route path="assistant/runs" element={<Guard perm="conversations.read"><RunsPage /></Guard>} />
                <Route path="assistant/runs/:id" element={<Guard perm="conversations.read"><RunDetailPage /></Guard>} />
                <Route path="assistant/feedback" element={<Guard perm="conversations.read"><FeedbackPage /></Guard>} />
                <Route path="usage" element={<Guard perm="usage.read"><UsagePage /></Guard>} />
                <Route path="admin/users" element={<Guard perm="iam.read"><UsersPage /></Guard>} />
                <Route path="admin/audit" element={<Guard perm="audit.read"><AuditPage /></Guard>} />
                <Route path="admin/settings" element={<Guard perm="settings.read"><SettingsPage /></Guard>} />
                <Route path="*" element={<EmptyState title="Sayfa bulunamadı" />} />
              </Routes>
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
